import { Components, Theme } from '@mui/material/styles';
import { Palette } from '@mui/material/styles/createPalette';

export const createMuiOverrides = (
  palette: Palette,
  darkTheme: boolean,
): Components<Omit<Theme, 'components'>> => ({
  // Apply styles to the global components
  MuiCssBaseline: {
    styleOverrides: {
      html: {
        fontSize: '14px',
      },
      body: {
        backgroundColor: palette.background.default,
        color: palette.text.primary,
      },
      a: {
        color: palette.primary.main,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiButton: {
    defaultProps: {
      color: 'secondary',
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
      },
    },
  },
  MuiInputLabel: {
    defaultProps: {
      shrink: true,
    },
    styleOverrides: {
      root: {
        position: 'relative' as const,
        transform: 'none',
        lineHeight: '20px',
        marginBottom: '4px',
        fontSize: '14px',
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: {
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'none',
        },
      },
    },
  },
  MuiInputBase: {
    defaultProps: {
      fullWidth: true,
    },
    styleOverrides: {
      sizeSmall: {
        // apply styles when Input size is "small"
        // fontSize: '14px', // make the font smaller
        // '& input': {
        //   paddingTop: "8.5px",
        //   paddingBottom: "8.5px",
        //   lineHeight: '20px',
        // },
        '& input': {
          height: '.9375rem',
          fontSize: 13,
        },
        '&.MuiInputBase-adornedStart': {
          paddingLeft: '8px',
        },
        '&.MuiInputBase-adornedEnd': {
          paddingRight: '8px',
        },
        '&& .MuiSelect-select': {
          padding: '4px 8px',
        },
      },
      root: {
        // background: hexToRgba(palette.grey[900], .06),
        lineHeight: 'inherit',
        '&::before': {
          display: 'none',
        },
        '&& .MuiInputAdornment-root:not(.MuiInputAdornment-hiddenLabel)': {
          marginTop: '0px!important',
        },
        '&.MuiInputBase-adornedStart': {
          paddingLeft: '14px',
        },
        '&.MuiInputBase-adornedEnd': {
          paddingRight: '14px',
        },
        '& input': {
          paddingTop: '8.5px',
          paddingBottom: '8.5px',
        },
        // placeholder color
        '&& ::placeholder': {
          color: palette.text.primary,
          opacity: 0.5,
          transition: '.3s ease',
        },
        '&:hover ::placeholder': {
          opacity: 1,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.divider,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.primary.light,
          top: 0,
          '& legend': {
            display: 'none',
          },
        },
        '.MuiOutlinedInput-notchedOutline': {
          top: 0,
          '& legend': {
            display: 'none',
          },
        },

        '&.MuiInputBase-adornedStart .MuiInputAdornment-root, &.MuiInputBase-adornedEnd .MuiInputAdornment-root':
          {
            '& svg': {
              color: palette.text.disabled,
              opacity: 1,
              // marginLeft: 8,
              // marginRight: 8,
            },
          },
        '&&&&.MuiInputBase-adornedEnd': {
          //paddingRight: "64px",
        },
        '&.Mui-focused.MuiInputBase-adornedStart .MuiInputAdornment-root, &.Mui-focused.MuiInputBase-adornedEnd .MuiInputAdornment-root':
          {
            '& svg': {
              color: palette.text.disabled,
            },
          },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: 'transparent',
      },
      notchedOutline: {
        borderColor: palette.divider,
        transition: 'border-color 0.2s ease-in-out',
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '&&&& input': {
          padding: 0,
        },
        '&& .MuiInputBase-adornedStart.MuiInputBase-root': {
          paddingLeft: '14px',
        },
        '&& .MuiInputBase-adornedEnd.MuiInputBase-root': {
          paddingRight: '14px',
        },
        '& .MuiInputAdornment-root, & .MuiInputAdornment-root': {
          height: '24px',
          width: '24px',
        },
        '&& ::placeholder': {
          opacity: 1,
          transition: '.3s ease',
        },
        '&:hover ::placeholder': {
          opacity: 1,
        },
        '& .MuiAutocomplete-endAdornment': {
          top: '50%',
          transform: 'translateY(-50%)',
        },
      },
    },
  },
  // style the select
  MuiSelect: {
    styleOverrides: {
      select: {
        padding: '8px 14px',
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        // TODO: Implement dark mode in a global way and remove this override
        color: darkTheme ? palette.text.primary : palette.text.primary,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        padding: '.5rem 1.5rem',
        textTransform: 'none',
      },
    },
  },
  MuiTooltip: {
    defaultProps: {
      arrow: true,
    },
    styleOverrides: {
      tooltip: {
        backgroundColor: palette.common.black,
        color: palette.common.white,
      },
      arrow: {
        color: palette.common.black,
      },
    },
  },
  MuiInput: {
    styleOverrides: {
      underline: {
        '&:before': {
          borderBottom: 'none',
        },
        '&:hover:not(.Mui-disabled):before': {
          borderBottom: 'none',
        },
        '&:after': {
          borderBottom: 'none',
        },
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      // Apply styles to the paper based component of the menu
      paper: {
        color: palette.text.primary, // Set the desired text color
        backgroundColor: palette.background.default, // Set the desired background color
      },
    },
  },

  MuiStepIcon: {
    styleOverrides: {
      root: {
        '&.Mui-active': {
          color: palette.primary.main, // Set the desired color for the completed step
        },
        '&.Mui-completed': {
          color: palette.text.disabled, // Set the desired color for the completed step
        },
      },
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      root: {
        '&.Mui-active': {
          color: palette.primary.main, // Set the desired color for the completed step
        },
        '&.Mui-completed': {
          color: palette.text.disabled, // Set the desired color for the completed step
        },
      },
    },
  },
});
